const UUID_LIKE_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ORGANISATION_ID_QUERY_PARAM = "organisationId";
const SUBSCRIPTION_ORG_ID_QUERY_PARAM = "orgId";
const SCOPED_ADMIN_PATH_PREFIXES = [
  "/revenue",
  "/quickbooks-trials",
  "/subscription",
  "/learning",
] as const;

/**
 * Resolves an organisation-scoped revenue access query from the current URL.
 *
 * @remarks
 * Revenue, learning, and related shell visibility gates are evaluated against
 * the admin revenue access context. Some of those gates only become visible
 * when an organisation scope is present, so route-level access checks need to
 * preserve a valid `organisationId` query parameter when one exists.
 */
export const resolveAdminRevenueAccessScope = (
  search: string,
): { organisationId: string } | undefined => {
  const organisationId =
    new URLSearchParams(search).get(ORGANISATION_ID_QUERY_PARAM)?.trim() ?? "";

  if (!UUID_LIKE_PATTERN.test(organisationId)) {
    return undefined;
  }

  return { organisationId };
};

const isScopedAdminPath = (pathname: string) =>
  SCOPED_ADMIN_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

/**
 * Preserves the current organisation-scoped revenue access context when the
 * operator navigates between scoped admin workspaces.
 *
 * @remarks
 * `PermissionProtectedRoute` resolves access visibility from the current URL,
 * so navigation inside the revenue or learning cluster must carry the same
 * `organisationId` when one is already active.
 */
export const buildAdminRevenueScopedPath = (
  path: string,
  currentSearch: string,
): string => {
  const scope = resolveAdminRevenueAccessScope(currentSearch);
  if (!scope) {
    return path;
  }

  const url = new URL(path, "http://localhost");
  if (!isScopedAdminPath(url.pathname)) {
    return path;
  }

  if (!url.searchParams.has(ORGANISATION_ID_QUERY_PARAM)) {
    url.searchParams.set(ORGANISATION_ID_QUERY_PARAM, scope.organisationId);
  }

  if (
    url.pathname.startsWith("/subscription") &&
    !url.searchParams.has(SUBSCRIPTION_ORG_ID_QUERY_PARAM)
  ) {
    url.searchParams.set(SUBSCRIPTION_ORG_ID_QUERY_PARAM, scope.organisationId);
  }

  const nextSearch = url.searchParams.toString();
  return `${url.pathname}${nextSearch ? `?${nextSearch}` : ""}${url.hash}`;
};
